import {
  HydratedOpportunity,
  OpportunityBoardFilters,
} from "@forgepoint/shared";
import { matchesArchiveMode, matchesBoardFilters } from "./opportunityFilters";
import type { OpportunityArchiveMode } from "./opportunityFilterTypes";

/** State of one kanban column: the loaded ids, the server total, and paging. */
export interface ColumnState {
  ids: string[];
  total: number;
  isLoading: boolean;
}

/**
 * Overlap subtracted from the delta cursor on each poll so an update whose
 * timestamp equals the cursor is never missed. Store merges are idempotent
 * by id, so re-fetching a row inside the overlap is harmless.
 */
export const DELTA_CURSOR_OVERLAP_MS = 1000;

/**
 * Returns the freshest updatedAt among the changed records, never earlier
 * than the current cursor.
 * @param changed - The changed opportunities from a delta poll
 * @param cursor - The current delta cursor
 * @returns The next delta cursor
 */
export function maxUpdatedAt(
  changed: HydratedOpportunity[],
  cursor: Date,
): Date {
  return changed.reduce(
    (max, opp) => (opp.updatedAt > max ? opp.updatedAt : max),
    cursor,
  );
}

/** Result of applying a live delta to the board's column state. */
export interface DeltaApplication {
  columns: Map<string, ColumnState>;
  /** True when any card entered, left, or moved between columns. */
  membershipChanged: boolean;
}

/**
 * Applies a batch of changed opportunities from the live delta poll to the
 * board's column state: cards that entered or moved are placed at the top of
 * their stage column, and cards that left the view are removed. The input map
 * is never mutated; a new map is returned only when something changed.
 * @param columns - The current per-stage column state, keyed by column id
 * @param changed - The changed opportunities reported by the delta poll
 * @param filters - The active board filters (archive flags are ignored here)
 * @param archiveMode - The board's archive view, deciding archived visibility
 * @returns The next column state and whether any membership changed
 */
export function applyOpportunityDeltaToColumns(
  columns: Map<string, ColumnState>,
  changed: HydratedOpportunity[],
  filters: OpportunityBoardFilters,
  archiveMode: OpportunityArchiveMode,
): DeltaApplication {
  const next = new Map(columns);
  const membershipChanged = changed.reduce((didChange, opp) => {
    return applyOne(next, opp, filters, archiveMode) || didChange;
  }, false);
  return { columns: membershipChanged ? next : columns, membershipChanged };
}

/**
 * Applies a single changed opportunity to the column map in place.
 * @param columns - The per-stage column state to mutate
 * @param opp - The changed opportunity
 * @param filters - The active board filters
 * @param archiveMode - The board's archive view
 * @returns True when the opportunity entered, left, or moved between columns
 */
function applyOne(
  columns: Map<string, ColumnState>,
  opp: HydratedOpportunity,
  filters: OpportunityBoardFilters,
  archiveMode: OpportunityArchiveMode,
): boolean {
  const targetKey = opp.stageId ?? opp.status;
  const currentKey = findColumnContaining(columns, opp.id);
  const belongs =
    columns.has(targetKey) &&
    matchesArchiveMode(opp, archiveMode) &&
    matchesBoardFilters(opp, filters);

  if (!belongs) {
    if (currentKey === undefined) {
      return false;
    }
    removeFromColumn(columns, currentKey, opp.id);
    return true;
  }

  if (currentKey === targetKey) {
    return false;
  }

  if (currentKey !== undefined) {
    removeFromColumn(columns, currentKey, opp.id);
  }
  insertAtTop(columns, targetKey, opp.id);
  return true;
}

/**
 * Returns the column with the id inserted at the top and the total bumped.
 * Idempotent: the delta poll can echo this client's own optimistic moves, so
 * an already-present id returns the column unchanged instead of duplicating.
 * @param column - The column to insert into
 * @param oppId - The opportunity id to insert
 * @returns The updated column, or the input column when nothing changed
 */
export function insertIdAtTop(column: ColumnState, oppId: string): ColumnState {
  if (column.ids.includes(oppId)) {
    return column;
  }
  return { ...column, ids: [oppId, ...column.ids], total: column.total + 1 };
}

/**
 * Returns the column with the id removed and the total decremented, or the
 * same column unchanged when the id is absent.
 * @param column - The column to remove from
 * @param oppId - The opportunity id to remove
 * @returns The updated column, or the input column when nothing changed
 */
export function removeId(column: ColumnState, oppId: string): ColumnState {
  if (!column.ids.includes(oppId)) {
    return column;
  }
  return {
    ...column,
    ids: column.ids.filter((id) => id !== oppId),
    total: Math.max(0, column.total - 1),
  };
}

/**
 * Finds the key of the column currently containing the opportunity.
 * @param columns - The per-stage column state
 * @param oppId - The opportunity id to look for
 * @returns The containing column's key, or undefined when absent
 */
function findColumnContaining(
  columns: Map<string, ColumnState>,
  oppId: string,
): string | undefined {
  for (const [key, column] of columns) {
    if (column.ids.includes(oppId)) {
      return key;
    }
  }
  return undefined;
}

/**
 * Removes the opportunity from the given column in place.
 * @param columns - The per-stage column state to mutate
 * @param key - The column to remove from
 * @param oppId - The opportunity id to remove
 */
function removeFromColumn(
  columns: Map<string, ColumnState>,
  key: string,
  oppId: string,
): void {
  const column = columns.get(key);
  if (!column) {
    return;
  }
  columns.set(key, removeId(column, oppId));
}

/**
 * Inserts the opportunity at the top of the given column in place.
 * @param columns - The per-stage column state to mutate
 * @param key - The column to insert into
 * @param oppId - The opportunity id to insert
 */
function insertAtTop(
  columns: Map<string, ColumnState>,
  key: string,
  oppId: string,
): void {
  const column = columns.get(key);
  if (!column) {
    return;
  }
  columns.set(key, insertIdAtTop(column, oppId));
}
